import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion, AnimatePresence } from "framer-motion";
import primaryComponents from "../../../primaryComponents";
import { WhatsAppIcon } from "../../../../assets/svgIcons/WhatsAppIcon";
import Icon from "../../../../assets/icons";
import { useInvoiceReady } from "./useInvoiceReady";
import styles from "./InvoiceReadyModal.module.scss";

const InvoiceReadyModal = ({
  orderDetails,
  invoiceUrl,
  closeModal = () => {},
}) => {
  const {
    customerName,
    formattedAmount,
    bankAccounts,
    isLoadingBankAccounts,
    activeBankAccount,
    storeDefaultBankAccount,
    selectedBankAccountId,
    setSelectedBankAccountId,
    expandedAction,
    handleToggleAction,
    emailInput,
    setEmailInput,
    phoneInput,
    setPhoneInput,
    isPhoneValid,
    isEmailValid,
    handlePrintInvoice,
    handleDownloadPdf,
    handleShareWhatsApp,
    handleSendEmail,
    isGeneratingInvoice,
    isPrintingInvoice,
    isDownloadingPdf,
    isSendingEmail,
  } = useInvoiceReady({ orderDetails, invoiceUrl, closeModal });

  const [showDropdown, setShowDropdown] = useState(false);

  const displayAccount = activeBankAccount || storeDefaultBankAccount;

  return (
    <motion.div
      initial={{ y: 60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={styles.invoiceReadyModal}
    >
      <div className="d-flex align-items-start justify-content-between">
        <div>
          <h4 className="font-weight-semibold m-0">Invoice Ready</h4>
          <p className="text-muted m-0 small">
            Choose how to share or download the invoice for this order.
          </p>
        </div>
        <FontAwesomeIcon
          onClick={closeModal}
          className="d-block ml-auto cursor-pointer"
          icon="times"
          style={{ fontSize: "20px" }}
        />
      </div>

      <div className={styles.invoiceReadyModal__summary}>
        <div className={styles.invoiceReadyModal__summary__row}>
          <span className="text-muted">Customer</span>
          <span className="font-weight-semibold dark--text">
            {customerName}
          </span>
        </div>
        <div className={styles.invoiceReadyModal__summary__row}>
          <span className="text-muted">Amount</span>
          <span className="font-weight-semibold dark--text">
            {formattedAmount}
          </span>
        </div>
      </div>

      <div className={styles.invoiceReadyModal__bankPicker}>
        <div
          className="d-flex align-items-center font-weight-semibold dark--text small"
          style={{ gap: "6px" }}
        >
          <span>Bank Account on Invoice</span>
          {selectedBankAccountId == null && storeDefaultBankAccount && (
            <span
              className="badge badge-pill mt-1"
              style={{
                background: "#ede9fe",
                color: "#7c3aed",
                fontWeight: 500,
                fontSize: "11px",
              }}
            >
              Store Default
            </span>
          )}
        </div>

        <div
          className={styles.invoiceReadyModal__bankPicker__current}
          onClick={() => setShowDropdown((prev) => !prev)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            e.key === "Enter" && setShowDropdown((prev) => !prev)
          }
        >
          <div className="d-flex flex-column" style={{ gap: "2px" }}>
            {isLoadingBankAccounts ? (
              <span className="text-muted small">Loading accounts...</span>
            ) : displayAccount ? (
              <>
                <span className="text-muted small">
                  {displayAccount.bank_name} · {displayAccount.account_number}
                </span>
                <strong className="dark--text small font-weight-semibold">
                  {displayAccount.account_name}
                </strong>
              </>
            ) : (
              <span className="text-muted small">
                No default account — will use company default
              </span>
            )}
          </div>
        </div>

        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
              className={styles.invoiceReadyModal__bankPicker__dropdown}
            >
              {storeDefaultBankAccount && (
                <div
                  className={`d-flex flex-column ${
                    styles.invoiceReadyModal__bankPicker__dropdown__item
                  }${selectedBankAccountId == null ? ` ${styles.active}` : ""}`}
                  onClick={() => {
                    setSelectedBankAccountId(null);
                    setShowDropdown(false);
                  }}
                  style={{ gap: "2px" }}
                >
                  <strong className="dark--text small font-weight-semibold">
                    {storeDefaultBankAccount.account_name}
                    <span
                      style={{
                        fontWeight: 400,
                        color: "#7c3aed",
                        marginLeft: 6,
                        fontSize: "0.72rem",
                      }}
                    >
                      Store Default
                    </span>
                  </strong>
                  <span className="text-muted small">
                    {storeDefaultBankAccount.bank_name} ·{" "}
                    {storeDefaultBankAccount.account_number}
                  </span>
                </div>
              )}

              {bankAccounts
                .filter((a) => a.id !== storeDefaultBankAccount?.id)
                .map((account) => (
                  <div
                    key={account.id}
                    className={`d-flex flex-column ${
                      styles.invoiceReadyModal__bankPicker__dropdown__item
                    }${
                      selectedBankAccountId === account.id
                        ? ` ${styles.active}`
                        : ""
                    }`}
                    onClick={() => {
                      setSelectedBankAccountId(account.id);
                      setShowDropdown(false);
                    }}
                    style={{ gap: "2px" }}
                  >
                    <strong className="dark--text small font-weight-semibold">
                      {account.account_name}
                    </strong>
                    <span className="text-muted small">
                      {account.bank_name} · {account.account_number}
                    </span>
                  </div>
                ))}

              {!isLoadingBankAccounts &&
                bankAccounts.length === 0 &&
                !storeDefaultBankAccount && (
                  <div
                    className={
                      styles.invoiceReadyModal__bankPicker__dropdown__item
                    }
                  >
                    <span className="text-muted small">
                      No bank accounts found
                    </span>
                  </div>
                )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="d-flex" style={{ gap: "10px" }}>
        <primaryComponents.Button
          classNames="btn btn--primary cover"
          onClick={handlePrintInvoice}
          isDisabled={isGeneratingInvoice}
          isLoading={isPrintingInvoice}
          loadingText="Printing..."
          id="invoice-print-btn"
          style={{ flex: 1 }}
        >
          <FontAwesomeIcon icon="print" style={{ marginRight: 6 }} />
          Print Invoice
        </primaryComponents.Button>

        <primaryComponents.Button
          classNames="btn btn--outline cover"
          onClick={handleDownloadPdf}
          isDisabled={isGeneratingInvoice}
          isLoading={isDownloadingPdf}
          loadingText="Downloading..."
          id="invoice-download-btn"
          style={{ flex: 1 }}
        >
          <FontAwesomeIcon icon="download" style={{ marginRight: 6 }} />
          Download PDF
        </primaryComponents.Button>
      </div>

      <div className="d-flex flex-column" style={{ gap: "10px" }}>
        <div className={styles.invoiceReadyModal__actionCard}>
          <div
            className="d-flex align-items-center justify-content-between p-3 bg-white cursor-pointer"
            onClick={() => handleToggleAction("whatsapp")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === "Enter" && handleToggleAction("whatsapp")
            }
          >
            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0 rounded"
                style={{
                  width: "36px",
                  height: "36px",
                  background: "#dcfce7",
                }}
              >
                <WhatsAppIcon width="20px" height="20px" />
              </div>
              <div className="d-flex flex-column">
                <strong className="dark--text small font-weight-semibold">
                  Send via WhatsApp
                </strong>
                <span className="text-muted small">
                  Opens WhatsApp with invoice link
                </span>
              </div>
            </div>
          </div>

          <AnimatePresence>
            {expandedAction === "whatsapp" && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ overflow: "hidden" }}
              >
                <div
                  className="p-3 light-bg border-top d-flex flex-column"
                  style={{ gap: "6px" }}
                >
                  <label className="font-weight-semibold dark--text small m-0">
                    Customer Phone Number
                  </label>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "8px" }}
                  >
                    <primaryComponents.InputField
                      placeholder="e.g. 08012345678"
                      value={phoneInput}
                      onChange={({ target: { value } }) => setPhoneInput(value)}
                      type="tel"
                      name="phone_for_whatsapp"
                      floatError={false}
                      classNames="smallInput"
                      bodyStyle={{ flex: 1 }}
                    />
                    <primaryComponents.Button
                      classNames="btn btn--primary smallBtn"
                      onClick={handleShareWhatsApp}
                      isDisabled={isGeneratingInvoice || !isPhoneValid}
                      isLoading={isGeneratingInvoice}
                      loadingText="Sending..."
                      style={{ flexShrink: 0 }}
                    >
                      Send
                    </primaryComponents.Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className={styles.invoiceReadyModal__actionCard}>
          <div
            className="d-flex align-items-center justify-content-between p-3 bg-white cursor-pointer"
            onClick={() => handleToggleAction("email")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleToggleAction("email")}
          >
            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0 rounded"
                style={{
                  width: "36px",
                  height: "36px",
                  background: "#ede9fe",
                }}
              >
                <img src={Icon.emailTwo} alt="email" width={20} height={20} />
              </div>
              <div className="d-flex flex-column">
                <strong className="dark--text small font-weight-semibold">
                  Send via Email
                </strong>
                <span className="text-muted small">
                  Deliver invoice to customer inbox
                </span>
              </div>
            </div>
          </div>

          <AnimatePresence>
            {expandedAction === "email" && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ overflow: "hidden" }}
              >
                <div
                  className="p-3 light-bg border-top d-flex flex-column"
                  style={{ gap: "6px" }}
                >
                  <label className="font-weight-semibold dark--text small m-0">
                    Customer Email
                  </label>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "8px" }}
                  >
                    <primaryComponents.InputField
                      placeholder="e.g. customer@example.com"
                      value={emailInput}
                      onChange={({ target: { value } }) => setEmailInput(value)}
                      type="email"
                      name="email_for_invoice"
                      floatError={false}
                      classNames="smallInput"
                      bodyStyle={{ flex: 1 }}
                    />

                    <primaryComponents.Button
                      classNames="btn btn--primary smallBtn"
                      onClick={handleSendEmail}
                      isDisabled={isSendingEmail || !isEmailValid}
                      isLoading={isSendingEmail}
                      loadingText="Sending..."
                      style={{ flexShrink: 0 }}
                    >
                      Send
                    </primaryComponents.Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default InvoiceReadyModal;
